<script setup>
import { h, ref, computed, onMounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { provideSidebarContext, useSidebarResize } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useWindowSize, useEventListener } from '@vueuse/core';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import SidebarChangelogCard from './SidebarChangelogCard.vue';
import SidebarChangelogButton from './SidebarChangelogButton.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import ChannelIcon from 'next/icon/ChannelIcon.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import {
  SIDEBAR_SORT_SECTIONS,
  getSidebarSortOptions,
  resolveSidebarSort,
  sortSidebarItems,
} from 'dashboard/helper/sidebarSort';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute, isOnChatwootCloud, currentAccount } = useAccount();
const route = useRoute();
const router = useRouter();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const isACustomBrandedInstance = useMapGetter(
  'globalConfig/isACustomBrandedInstance'
);
const isRTL = useMapGetter('accounts/isRTL');

const { width: windowWidth } = useWindowSize();
const isMobile = computed(() => windowWidth.value < 768);

const accountId = useMapGetter('getCurrentAccountId');
const currentUserId = useMapGetter('getCurrentUserID');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// Per-account Super Admin toggle — "API Não Oficial" (WAHA) is opt-in, off
// by default. See Metrica::AccountPlan#waha_unofficial_enabled?.
const wahaUnofficialEnabled = computed(
  () => currentAccount.value?.waha_unofficial_enabled === true
);

// Per-account Super Admin toggles for the two vertical modules — assisted-sale
// add-ons, off by default. Unlike the rest of the product (where a gated
// feature is shown and the backend 403s with an upgrade prompt), these are
// hidden outright: there is no self-serve way to buy them, so an upgrade
// prompt would be a dead end. See Metrica::AccountPlan#hospedagem_available? /
// #passeios_available?.
const hospedagemEnabled = computed(
  () => currentAccount.value?.hospedagem_enabled === true
);
const passeiosEnabled = computed(
  () => currentAccount.value?.passeios_enabled === true
);

const hasAdvancedAssignment = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.ADVANCED_ASSIGNMENT
  );
});

const hasConversationUnreadCounts = computed(() => {
  return isFeatureEnabledonAccount.value(
    accountId.value,
    FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS
  );
});

const fetchConversationUnreadCounts = ([currentAccountId, isEnabled]) => {
  if (!currentAccountId) return;

  if (!isEnabled) {
    store.dispatch('conversationUnreadCounts/clear');
    return;
  }

  store.dispatch('conversationUnreadCounts/get');
};

const fetchSidebarSortPreferences = ([currentAccountId, userId]) => {
  if (!currentAccountId || !userId) return;
  store.dispatch('sidebarSortPreferences/initialize');
};

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = ref(null);

// [metrica] Toggles the given group open/closed (2026-07-23). An earlier fix
// (c7f9538b4) made this only ever open and never close on a second click,
// because closing used to leave a single orphaned leaf visible — SidebarGroup
// v-show kept just the active child as a "you are here" breadcrumb even
// while "closed", which read as "every sibling vanished but one". That
// breadcrumb peek is what was actually broken; it's removed now (see
// SidebarGroup.vue's ul/leaf v-show, keyed on isExpanded alone), so a closed
// group hides ALL of its children again, cleanly, and the toggle is safe to
// restore — reported as a regression by the user, who explicitly still wants
// retracting a group to work.
const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};

const {
  sidebarWidth,
  isCollapsed,
  setSidebarWidth,
  saveWidth,
  snapToCollapsed,
  snapToExpanded,
  COLLAPSED_THRESHOLD,
} = useSidebarResize();

// On mobile, sidebar is always expanded (flyout mode)
const isEffectivelyCollapsed = computed(
  () => !isMobile.value && isCollapsed.value
);

// Resize handle logic
const isResizing = ref(false);
const startX = ref(0);
const startWidth = ref(0);

provideSidebarContext({
  expandedItem,
  setExpandedItem,
  isCollapsed: isEffectivelyCollapsed,
  sidebarWidth,
  isResizing,
});

// Get clientX from mouse or touch event
const getClientX = event =>
  event.touches ? event.touches[0].clientX : event.clientX;

const onResizeStart = event => {
  isResizing.value = true;
  startX.value = getClientX(event);
  startWidth.value = sidebarWidth.value;
  Object.assign(document.body.style, {
    cursor: 'col-resize',
    userSelect: 'none',
  });
  // Prevent default to avoid scrolling on touch
  event.preventDefault();
};

const onResizeMove = event => {
  if (!isResizing.value) return;

  const delta = isRTL.value
    ? startX.value - getClientX(event)
    : getClientX(event) - startX.value;
  setSidebarWidth(startWidth.value + delta);
};

const onResizeEnd = () => {
  if (!isResizing.value) return;

  isResizing.value = false;
  Object.assign(document.body.style, { cursor: '', userSelect: '' });

  // Snap to collapsed state if below threshold
  if (sidebarWidth.value < COLLAPSED_THRESHOLD) {
    snapToCollapsed();
  } else {
    saveWidth();
  }
};

const onResizeHandleDoubleClick = () => {
  if (isCollapsed.value) snapToExpanded();
  else snapToCollapsed();
};

// Drag-to-resize alone is a low-discoverability affordance for collapsing the
// sidebar — this exposes the same toggle (onResizeHandleDoubleClick) as an
// explicit clickable button. Chevron points "away from center" when
// collapsed (invites expand) and "toward center" when expanded (invites
// collapse); mirrored for RTL, where the sidebar sits on the right. Only the
// BOOLEAN is computed — the icon classes themselves stay as static string
// literals in the template (v-if/v-else, same as the chevron-up toggle in
// SidebarGroupHeader.vue), not funneled through a dynamic :class string.
const collapseIconPointsRight = computed(
  () => isEffectivelyCollapsed.value !== isRTL.value
);

// Support both mouse and touch events
useEventListener(document, 'mousemove', onResizeMove);
useEventListener(document, 'mouseup', onResizeEnd);
useEventListener(document, 'touchmove', onResizeMove, { passive: false });
useEventListener(document, 'touchend', onResizeEnd);

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const allUnreadCount = useMapGetter(
  'conversationUnreadCounts/getAllUnreadCount'
);
const getInboxUnreadCount = useMapGetter(
  'conversationUnreadCounts/getInboxUnreadCount'
);
const getLabelUnreadCount = useMapGetter(
  'conversationUnreadCounts/getLabelUnreadCount'
);
const getTeamUnreadCount = useMapGetter(
  'conversationUnreadCounts/getTeamUnreadCount'
);
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);
const getSidebarSectionSort = useMapGetter(
  'sidebarSortPreferences/getSectionSort'
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
});

watch([accountId, hasConversationUnreadCounts], fetchConversationUnreadCounts, {
  immediate: true,
});

watch([accountId, currentUserId], fetchSidebarSortPreferences, {
  immediate: true,
});

const getSortOptionsForSection = section =>
  getSidebarSortOptions(section, {
    hasUnreadCounts: hasConversationUnreadCounts.value,
  });

const getSortForSection = section =>
  resolveSidebarSort(section, getSidebarSectionSort.value(section), {
    hasUnreadCounts: hasConversationUnreadCounts.value,
  });

const updateSortPreference = (section, sortBy) => {
  store.dispatch('sidebarSortPreferences/setSectionSort', {
    section,
    sortBy,
  });
};

const buildSortConfig = section => ({
  sortOptions: getSortOptionsForSection(section),
  activeSort: getSortForSection(section),
  onSortChange: sortBy => updateSortPreference(section, sortBy),
});

const sortedFolders = computed(() =>
  sortSidebarItems(conversationCustomViews.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.FOLDERS),
    labelKey: view => view.name,
  })
);

const sortedTeams = computed(() =>
  sortSidebarItems(teams.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.TEAMS),
    labelKey: team => team.name,
    unreadCountKey: team => getTeamUnreadCount.value(team.id),
  })
);

const sortedInboxes = computed(() =>
  sortSidebarItems(inboxes.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.CHANNELS),
    labelKey: inbox => inbox.name,
    unreadCountKey: inbox => getInboxUnreadCount.value(inbox.id),
  })
);

const sortedLabels = computed(() =>
  sortSidebarItems(labels.value, {
    sortBy: getSortForSection(SIDEBAR_SORT_SECTIONS.LABELS),
    labelKey: label => label.title,
    unreadCountKey: label => getLabelUnreadCount.value(label.id),
  })
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const newReportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
];

const reportRoutes = computed(() => newReportRoutes());

const menuItems = computed(() => {
  return [
    // [metrica] Item "Caixa de Entrada" (overview) ocultado a pedido do
    // usuário — redundante com "Conversas" logo abaixo. Rota mantida
    // (upstream-safe), só fora do menu.
    // {
    //   name: 'Inbox',
    //   label: t('SIDEBAR.INBOX'),
    //   icon: 'i-lucide-inbox',
    //   to: accountScopedRoute('inbox_view'),
    //   activeOn: ['inbox_view', 'inbox_view_conversation'],
    //   getterKeys: {
    //     count: 'notifications/getUnreadCount',
    //   },
    // },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          icon: 'i-lucide-inbox',
          badgeCount: allUnreadCount.value,
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          icon: 'i-lucide-at-sign',
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Participating',
          label: t('SIDEBAR.PARTICIPATING_CONVERSATIONS'),
          icon: 'i-lucide-user-round-check',
          activeOn: ['conversation_through_participating'],
          to: accountScopedRoute('conversation_participating'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          icon: 'i-lucide-clock-alert',
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.FOLDERS),
          collapsible: true,
          showTreeLine: true,
          children: sortedFolders.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute('folder_conversations', { id: view.id }),
          })),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.TEAMS),
          collapsible: true,
          showTreeLine: true,
          children: sortedTeams.value.map(team => ({
            name: `${team.name}-${team.id}`,
            label: team.name,
            badgeCount: getTeamUnreadCount.value(team.id),
            to: accountScopedRoute('team_conversations', { teamId: team.id }),
          })),
        },
        {
          name: 'Channels',
          label: t('SIDEBAR.CHANNELS'),
          icon: 'i-lucide-mailbox',
          activeOn: ['conversation_through_inbox'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.CHANNELS),
          collapsible: true,
          showTreeLine: true,
          children: sortedInboxes.value.map(inbox => ({
            name: `${inbox.name}-${inbox.id}`,
            label: inbox.name,
            badgeCount: getInboxUnreadCount.value(inbox.id),
            icon: h(ChannelIcon, { inbox, class: 'size-[16px]' }),
            to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
            component: leafProps =>
              h(ChannelLeaf, {
                label: leafProps.label,
                active: leafProps.active,
                inbox,
                badgeCount: leafProps.badgeCount,
              }),
          })),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          ...buildSortConfig(SIDEBAR_SORT_SECTIONS.LABELS),
          collapsible: true,
          showTreeLine: true,
          children: sortedLabels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            badgeCount: getLabelUnreadCount.value(label.id),
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute('label_conversations', {
              label: label.title,
            }),
          })),
        },
      ],
    },
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute(
            'contacts_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          collapsible: true,
          showTreeLine: true,
          children: contactCustomViews.value.map(view => ({
            name: `${view.name}-${view.id}`,
            label: view.name,
            to: accountScopedRoute(
              'contacts_dashboard_segments_index',
              { segmentId: view.id },
              { page: 1 }
            ),
            activeOn: [
              'contacts_dashboard_segments_index',
              'contacts_edit_segment',
            ],
          })),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          collapsible: true,
          showTreeLine: true,
          children: labels.value.map(label => ({
            name: `${label.title}-${label.id}`,
            label: label.title,
            icon: h('span', {
              class: `size-[8px] rounded-sm`,
              style: { backgroundColor: label.color },
            }),
            to: accountScopedRoute(
              'contacts_dashboard_labels_index',
              { label: label.title },
              { page: 1, search: undefined }
            ),
            activeOn: [
              'contacts_dashboard_labels_index',
              'contacts_edit_label',
            ],
          })),
        },
      ],
    },
    {
      name: 'Companies',
      label: t('SIDEBAR.COMPANIES'),
      icon: 'i-lucide-building-2',
      children: [
        {
          name: 'All Companies',
          label: t('SIDEBAR.ALL_COMPANIES'),
          to: accountScopedRoute(
            'companies_dashboard_index',
            {},
            { page: 1, search: undefined }
          ),
          activeOn: ['companies_dashboard_index', 'companies_dashboard_show'],
        },
      ],
    },
    {
      name: 'Metrica CRM',
      label: t('METRICA_CRM.TITLE'),
      icon: 'i-lucide-kanban',
      children: [
        {
          name: 'Metrica CRM Funnel',
          label: t('METRICA_CRM.TAB_FUNNEL'),
          to: accountScopedRoute('metrica_crm'),
          activeOn: ['metrica_crm'],
        },
        {
          name: 'Metrica CRM Tasks',
          label: t('METRICA_CRM.TAB_TASKS'),
          to: accountScopedRoute('metrica_crm_tasks'),
          activeOn: ['metrica_crm_tasks'],
        },
        {
          name: 'Metrica CRM Reports',
          label: t('METRICA_CRM.TAB_REPORTS'),
          to: accountScopedRoute('metrica_crm_reports'),
          activeOn: ['metrica_crm_reports'],
        },
      ],
    },
    {
      name: 'Metrica Whatsapp Campaigns',
      label: t('METRICA_WHATSAPP_CAMPAIGNS.TITLE'),
      icon: 'i-lucide-megaphone',
      children: [
        // [metrica] Só subgrupos (sem filho-folha direto). Ao clicar no pai, o
        // SidebarGroup navega para o primeiro leaf acessível — que é "Modelos de
        // Mensagem" (1º item de "API Não Oficial"), e o subgrupo já vem expandido
        // por padrão, então o destino fica visível/destacado. Sem atalho
        // redundante no topo.
        // "API Não Oficial" só aparece quando o super admin liberou a flag
        // pra esta organização (opt-in, off por padrão) — "API Oficial" nunca
        // é afetada.
        ...(wahaUnofficialEnabled.value
          ? [
              {
                name: 'Metrica Whatsapp Unofficial Group',
                label: t('METRICA_WHATSAPP_CAMPAIGNS.GROUPS.UNOFFICIAL'),
                collapsible: true,
                showTreeLine: true,
                children: [
                  {
                    name: 'Metrica Whatsapp Templates',
                    label: t('METRICA_WHATSAPP_CAMPAIGNS.TABS.TEMPLATES'),
                    to: accountScopedRoute('metrica_whatsapp_templates'),
                    activeOn: ['metrica_whatsapp_templates'],
                  },
                  {
                    name: 'Metrica Whatsapp Campaigns List',
                    label: t('METRICA_WHATSAPP_CAMPAIGNS.TABS.CAMPAIGNS'),
                    to: accountScopedRoute('metrica_whatsapp_campaigns'),
                    activeOn: ['metrica_whatsapp_campaigns'],
                  },
                ],
              },
            ]
          : []),
        {
          name: 'Metrica Whatsapp Official Group',
          label: t('METRICA_WHATSAPP_CAMPAIGNS.GROUPS.OFFICIAL'),
          collapsible: true,
          showTreeLine: true,
          children: [
            {
              name: 'Metrica Whatsapp Official Campaigns List',
              label: t('METRICA_WHATSAPP_CAMPAIGNS.TABS.CAMPAIGNS'),
              to: accountScopedRoute('metrica_whatsapp_official_campaigns'),
              activeOn: ['metrica_whatsapp_official_campaigns'],
            },
          ],
        },
      ],
    },
    {
      name: 'Metrica Scripts',
      label: t('METRICA_SCRIPTS.TITLE'),
      icon: 'i-lucide-message-square-quote',
      to: accountScopedRoute('metrica_scripts'),
      activeOn: ['metrica_scripts'],
    },
    {
      name: 'Metrica AI Team',
      label: t('METRICA_AI_AGENTS.TITLE'),
      icon: 'i-lucide-bot',
      children: [
        {
          name: 'Metrica AI Agents',
          label: t('METRICA_AI_AGENTS.TABS.AGENTS'),
          to: accountScopedRoute('metrica_ai_agents'),
          activeOn: ['metrica_ai_agents'],
        },
        {
          name: 'Metrica AI Rules',
          label: t('METRICA_AI_AGENTS.TABS.GOLDEN_RULES'),
          to: accountScopedRoute('metrica_ai_agents_rules'),
          activeOn: ['metrica_ai_agents_rules'],
        },
        {
          name: 'Metrica AI Knowledge',
          label: t('METRICA_AI_AGENTS.TABS.KNOWLEDGE'),
          to: accountScopedRoute('metrica_ai_agents_knowledge'),
          activeOn: ['metrica_ai_agents_knowledge'],
        },
        {
          name: 'Metrica AI History',
          label: t('METRICA_AI_AGENTS.TABS.HISTORY'),
          to: accountScopedRoute('metrica_ai_agents_history'),
          activeOn: ['metrica_ai_agents_history'],
        },
      ],
    },
    {
      name: 'Metrica AI Settings',
      label: t('METRICA_AI_SETTINGS.TITLE'),
      icon: 'i-lucide-key-round',
      to: accountScopedRoute('metrica_ai_settings'),
      activeOn: ['metrica_ai_settings'],
    },
    {
      name: 'Metrica Resources',
      label: t('METRICA_RESOURCES.TITLE'),
      icon: 'i-lucide-plug-zap',
      children: [
        {
          name: 'Metrica Resource Agents',
          label: t('METRICA_RESOURCES.TABS.AGENTS'),
          to: accountScopedRoute('metrica_resource_agents'),
          activeOn: ['metrica_resource_agents'],
        },
        {
          name: 'Metrica Resource Connections',
          label: t('METRICA_RESOURCES.TABS.CONNECTIONS'),
          to: accountScopedRoute('metrica_resource_connections'),
          activeOn: ['metrica_resource_connections'],
        },
        ...(hospedagemEnabled.value
          ? [
              {
                name: 'Metrica Hotel Properties',
                label: t('METRICA_RESOURCES.TABS.PROPERTIES'),
                to: accountScopedRoute('metrica_hotel_properties'),
                activeOn: ['metrica_hotel_properties'],
              },
            ]
          : []),
        ...(passeiosEnabled.value
          ? [
              {
                name: 'Metrica Paytour Stores',
                label: t('METRICA_RESOURCES.TABS.PAYTOUR'),
                to: accountScopedRoute('metrica_paytour_stores'),
                activeOn: ['metrica_paytour_stores'],
              },
            ]
          : []),
        {
          name: 'Metrica Resource History',
          label: t('METRICA_RESOURCES.TABS.HISTORY'),
          to: accountScopedRoute('metrica_resource_agents_history'),
          activeOn: ['metrica_resource_agents_history'],
        },
      ],
    },
    {
      name: 'Metrica Prompt Examples',
      label: t('METRICA_PROMPT_EXAMPLES.TITLE'),
      icon: 'i-lucide-message-square-text',
      to: accountScopedRoute('metrica_prompt_examples'),
      activeOn: ['metrica_prompt_examples'],
    },
    {
      // [metrica] Consolida os 3 itens de topo que antes existiam separados
      // ("Raio-X", "Insights IA" e o "Relatórios" nativo) em um único grupo,
      // evitando fragmentar a mesma categoria mental (relatórios) em 3
      // entradas de menu. Raio-X e Insights IA continuam como acesso direto
      // de 1 clique; os relatórios nativos ficam num subgrupo colapsável
      // (mesmo padrão já usado em Conversas e Campanhas WhatsApp).
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: [
        {
          name: 'Metrica Agents',
          label: t('METRICA_AGENTS.TITLE'),
          icon: 'i-lucide-radar',
          to: accountScopedRoute('metrica_agents'),
          activeOn: ['metrica_agents'],
        },
        {
          name: 'Metrica Reports',
          label: t('METRICA_REPORTS.TITLE'),
          icon: 'i-lucide-lightbulb',
          to: accountScopedRoute('metrica_reports'),
          activeOn: ['metrica_reports'],
        },
        {
          name: 'Reports Operational',
          label: t('METRICA_REPORTS_HUB.OPERATIONAL_GROUP'),
          collapsible: true,
          showTreeLine: true,
          children: [
            {
              name: 'Report Overview',
              label: t('SIDEBAR.REPORTS_OVERVIEW'),
              to: accountScopedRoute('account_overview_reports'),
            },
            {
              name: 'Report Conversation',
              label: t('SIDEBAR.REPORTS_CONVERSATION'),
              to: accountScopedRoute('conversation_reports'),
            },
            ...reportRoutes.value,
            {
              name: 'Reports CSAT',
              label: t('SIDEBAR.CSAT'),
              to: accountScopedRoute('csat_reports'),
            },
            {
              name: 'Reports SLA',
              label: t('SIDEBAR.REPORTS_SLA'),
              to: accountScopedRoute('sla_reports'),
            },
            // [metrica] Relatório "Bot" nativo ocultado para não confundir com a
            // nossa "Equipe de IA". Rota mantida (upstream-safe), só fora do menu.
            // {
            //   name: 'Reports Bot',
            //   label: t('SIDEBAR.REPORTS_BOT'),
            //   to: accountScopedRoute('bot_reports'),
            // },
          ],
        },
      ],
    },
    // [metrica] Item "Campaigns" nativo ocultado a pedido do usuário —
    // substituído pela nossa "Campanhas WhatsApp" (WAHA). Rota mantida
    // (upstream-safe), só fora do menu.
    // {
    //   name: 'Campaigns',
    //   label: t('SIDEBAR.CAMPAIGNS'),
    //   icon: 'i-lucide-megaphone',
    //   children: [
    //     {
    //       name: 'Live chat',
    //       label: t('SIDEBAR.LIVE_CHAT'),
    //       to: accountScopedRoute('campaigns_livechat_index'),
    //     },
    //     {
    //       name: 'SMS',
    //       label: t('SIDEBAR.SMS'),
    //       to: accountScopedRoute('campaigns_sms_index'),
    //     },
    //     {
    //       name: 'WhatsApp',
    //       label: t('SIDEBAR.WHATSAPP'),
    //       to: accountScopedRoute('campaigns_whatsapp_index'),
    //     },
    //   ],
    // },
    {
      name: 'Metrica Billing',
      label: t('METRICA_BILLING.TITLE'),
      icon: 'i-lucide-credit-card',
      to: accountScopedRoute('metrica_billing'),
      activeOn: ['metrica_billing'],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      // [metrica] Sub-agrupado em 3 blocos colapsáveis (2026-07-20, achado do
      // levantamento de design: 15+ itens numa lista plana única, o grupo mais
      // denso da sidebar). Reusa o padrão collapsible+showTreeLine já usado em
      // Conversas (Pastas/Times/Canais/Etiquetas) e Campanhas WhatsApp (API Não
      // Oficial/Oficial) — SidebarSubGroup.vue já tem seu próprio colapso
      // persistido em localStorage, nada novo precisou ser construído aqui.
      children: [
        {
          name: 'Settings Group General',
          label: t('METRICA_SIDEBAR.SETTINGS_GROUP_GENERAL'),
          collapsible: true,
          showTreeLine: true,
          children: [
            {
              name: 'Settings Account Settings',
              label: t('SIDEBAR.ACCOUNT_SETTINGS'),
              icon: 'i-lucide-briefcase',
              to: accountScopedRoute('general_settings_index'),
            },
            // {
            //   name: 'Settings Captain',
            //   label: t('SIDEBAR.CAPTAIN_AI'),
            //   icon: 'i-woot-captain',
            //   to: accountScopedRoute('captain_settings_index'),
            // },
            {
              name: 'Settings Agents',
              label: t('SIDEBAR.AGENTS'),
              icon: 'i-lucide-square-user',
              to: accountScopedRoute('agent_list'),
            },
            {
              name: 'Settings Teams',
              label: t('SIDEBAR.TEAMS'),
              icon: 'i-lucide-users',
              activeOn: [
                'settings_teams_list',
                'settings_teams_new',
                'settings_teams_finish',
                'settings_teams_add_agents',
                'settings_teams_show',
                'settings_teams_edit',
                'settings_teams_edit_members',
                'settings_teams_edit_finish',
              ],
              to: accountScopedRoute('settings_teams_list'),
            },
            ...(hasAdvancedAssignment.value
              ? [
                  {
                    name: 'Settings Agent Assignment',
                    label: t('SIDEBAR.AGENT_ASSIGNMENT'),
                    icon: 'i-lucide-user-cog',
                    activeOn: [
                      'assignment_policy_index',
                      'agent_assignment_policy_index',
                      'agent_assignment_policy_create',
                      'agent_assignment_policy_edit',
                      'agent_capacity_policy_index',
                      'agent_capacity_policy_create',
                      'agent_capacity_policy_edit',
                    ],
                    to: accountScopedRoute('assignment_policy_index'),
                  },
                ]
              : []),
          ],
        },
        {
          name: 'Settings Group Channels',
          label: t('METRICA_SIDEBAR.SETTINGS_GROUP_CHANNELS'),
          collapsible: true,
          showTreeLine: true,
          children: [
            {
              name: 'Settings Inboxes',
              label: t('SIDEBAR.INBOXES'),
              icon: 'i-lucide-inbox',
              activeOn: [
                'settings_inbox_list',
                'settings_inbox_show',
                'settings_inbox_new',
                'settings_inbox_finish',
                'settings_inboxes_page_channel',
                'settings_inboxes_add_agents',
              ],
              to: accountScopedRoute('settings_inbox_list'),
            },
            {
              name: 'Settings Labels',
              label: t('SIDEBAR.LABELS'),
              icon: 'i-lucide-tags',
              to: accountScopedRoute('labels_list'),
            },
            {
              name: 'Settings Custom Attributes',
              label: t('SIDEBAR.CUSTOM_ATTRIBUTES'),
              icon: 'i-lucide-code',
              to: accountScopedRoute('attributes_list'),
            },
            {
              name: 'Settings Automation',
              label: t('SIDEBAR.AUTOMATION'),
              icon: 'i-lucide-repeat',
              to: accountScopedRoute('automation_list'),
            },
            // [metrica] Config "Agent Bots" nativa ocultada para não confundir com
            // a nossa "Equipe de IA". Rota mantida (upstream-safe), só fora do menu.
            // {
            //   name: 'Settings Agent Bots',
            //   label: t('SIDEBAR.AGENT_BOTS'),
            //   icon: 'i-lucide-bot',
            //   to: accountScopedRoute('agent_bots'),
            // },
            {
              name: 'Settings Macros',
              label: t('SIDEBAR.MACROS'),
              icon: 'i-lucide-toy-brick',
              to: accountScopedRoute('macros_wrapper'),
            },
            {
              name: 'Settings Canned Responses',
              label: t('SIDEBAR.CANNED_RESPONSES'),
              icon: 'i-lucide-message-square-quote',
              to: accountScopedRoute('canned_list'),
            },
            {
              name: 'Settings Integrations',
              label: t('SIDEBAR.INTEGRATIONS'),
              icon: 'i-lucide-blocks',
              to: accountScopedRoute('settings_applications'),
            },
            {
              name: 'Conversation Workflow',
              label: t('SIDEBAR.CONVERSATION_WORKFLOW'),
              icon: 'i-lucide-workflow',
              to: accountScopedRoute('conversation_workflow_index'),
            },
          ],
        },
        {
          name: 'Settings Group Security',
          label: t('METRICA_SIDEBAR.SETTINGS_GROUP_SECURITY'),
          collapsible: true,
          showTreeLine: true,
          children: [
            {
              name: 'Settings Audit Logs',
              label: t('SIDEBAR.AUDIT_LOGS'),
              icon: 'i-lucide-briefcase',
              to: accountScopedRoute('auditlogs_list'),
            },
            {
              name: 'Settings Custom Roles',
              label: t('SIDEBAR.CUSTOM_ROLES'),
              icon: 'i-lucide-shield-plus',
              to: accountScopedRoute('custom_roles_list'),
            },
            {
              name: 'Settings Sla',
              label: t('SIDEBAR.SLA'),
              icon: 'i-lucide-clock-alert',
              to: accountScopedRoute('sla_list'),
            },
            {
              name: 'Settings Security',
              label: t('SIDEBAR.SECURITY'),
              icon: 'i-lucide-shield',
              to: accountScopedRoute('security_settings_index'),
            },
            // [metrica] "Cobrança" nativa ocultada — essa tela é do billing do
            // Chatwoot Cloud (lê customAttributes.plan_name e limites do Captain
            // AI), sem nenhuma relação com o billing real do Prime Insight (Stripe,
            // ver "Assinatura"). Mantida comentada para não duplicar/confundir
            // com "Assinatura". Rota mantida (upstream-safe) — outras telas
            // (paywalls/upgrade CTAs) continuam linkando direto para ela.
            // {
            //   name: 'Settings Billing',
            //   label: t('SIDEBAR.BILLING'),
            //   icon: 'i-lucide-credit-card',
            //   to: accountScopedRoute('billing_settings_index'),
            // },
          ],
        },
      ],
    },
  ];
});

// [metrica] Groups the top-level menuItems into labeled/divided sections for
// the sidebar's information architecture, without touching any item's data.
// Items keep their exact object literals from menuItems — this just looks
// each one up by name and buckets it under a section.
//
// [metrica 2026-07-27] Reorganizado por um painel de design (15 agentes de
// investigação/proposta/crítica): cada seção responde a UMA pergunta do
// usuário, não a uma categoria técnica. Isso dissolveu a antiga seção órfã
// "Central de Ajuda" (tinha 1 item só) e partiu o balde-genérico "Conta"
// (analytics + billing + setup juntos) em "Gestão" (o que o dono monitora:
// relatórios + assinatura) e "Administração" (todo o setup, afundado no fim:
// provedor de IA + configurações). Perfil de tamanho foi de 4/2/3/1/3 para
// 3/3/3/2/2 — nenhuma seção órfã, tamanhos parelhos (Lei de Miller).
//
// Section icons are written as literal strings here (never assembled at
// runtime) so Tailwind/Iconify's source scanner picks them up — the same
// mechanism `menuItems` already relies on. Each is checked against the icons
// the nav items themselves use, so a section never wears a child's icon:
// `bot`/`plug-zap`/`message-square-text` pertencem aos itens de Automação & IA;
// `chart-spline`/`credit-card` aos de Gestão; `key-round`/`bolt` aos de
// Administração. `gauge` e `sliders-horizontal` são distintos de todos eles e
// ambos JÁ existem como literais no bundle (MetricaAgents.vue / MetricaCrm.vue),
// então não viram gap vazio de 14px.
const SIDEBAR_SECTIONS = [
  {
    key: 'service',
    labelKey: 'METRICA_SIDEBAR.SECTION_SERVICE',
    icon: 'i-lucide-headset',
    // [metrica] Scripts (biblioteca de argumentos de venda/atendimento) mora
    // aqui, logo abaixo de Contatos, por ser conteúdo que quem atende consulta
    // no dia a dia — não é config de automação/IA como o resto de 'automation'.
    itemNames: ['Conversation', 'Contacts', 'Metrica Scripts'],
  },
  {
    key: 'sales',
    labelKey: 'METRICA_SIDEBAR.SECTION_SALES',
    icon: 'i-lucide-trending-up',
    // [metrica] Fluxo de venda em ordem: o funil/negócio (CRM), a conta por
    // trás do lead (Empresas — veio de Atendimento) e o disparo em massa
    // (Campanhas WhatsApp).
    itemNames: ['Metrica CRM', 'Companies', 'Metrica Whatsapp Campaigns'],
  },
  {
    key: 'automation',
    labelKey: 'METRICA_SIDEBAR.SECTION_AUTOMATION',
    icon: 'i-lucide-sparkles',
    // [metrica] A IA que trabalha por você: o time de agentes, os recursos que
    // ele consulta e os exemplos de prompt que guiam o uso (vieram da extinta
    // "Central de Ajuda"). O SETUP técnico da IA (chave/provedor) saiu daqui e
    // afundou em Administração, junto do resto do setup.
    itemNames: [
      'Metrica AI Team',
      'Metrica Resources',
      'Metrica Prompt Examples',
    ],
  },
  {
    key: 'management',
    labelKey: 'METRICA_SIDEBAR.SECTION_MANAGEMENT',
    icon: 'i-lucide-gauge',
    // [metrica] O que o dono/gestor monitora e decide. Assinatura sai de baixo
    // de um rótulo administrativo e passa à linha de visão do dono (descoberta
    // de upgrade), ao lado dos relatórios.
    itemNames: ['Reports', 'Metrica Billing'],
  },
  {
    key: 'admin',
    labelKey: 'METRICA_SIDEBAR.SECTION_ADMIN',
    icon: 'i-lucide-sliders-horizontal',
    // [metrica] Todo o setup, afundado no fim: provedor de IA (chave) e as
    // configurações gerais/canais/segurança.
    itemNames: ['Metrica AI Settings', 'Settings'],
  },
];

const menuSections = computed(() => {
  const itemsByName = new Map(menuItems.value.map(item => [item.name, item]));

  // This rebuilds the section objects field by field, so anything added to
  // SIDEBAR_SECTIONS has to be forwarded here explicitly or it silently
  // disappears — which is exactly how `icon` shipped as an empty 14px gap the
  // first time.
  return SIDEBAR_SECTIONS.map(section => ({
    key: section.key,
    labelKey: section.labelKey,
    icon: section.icon,
    items: section.itemNames.map(name => itemsByName.get(name)).filter(Boolean),
  }));
});

// [metrica] Mirrors the active-route matching SidebarGroup.vue already does for
// its own nested groups (see `isActive`/`activeChild` there) -- reused here at
// the ROOT level so a top section can auto-open when the active route lives
// inside it (see the watch below this section's storage helpers).
const resolveItemPath = to => (to ? router.resolve(to)?.path || '/' : '/');

const itemMatchesActiveRoute = item => {
  if (
    item.to &&
    (route.path === resolveItemPath(item.to) ||
      item.activeOn?.includes(route.name))
  ) {
    return true;
  }
  return item.children?.some(itemMatchesActiveRoute) ?? false;
};

const activeSectionKey = computed(
  () =>
    menuSections.value.find(section =>
      section.items.some(itemMatchesActiveRoute)
    )?.key ?? null
);

// [metrica] Persisted collapse state for the top-level sections above, mirroring
// SidebarSubGroup.vue's own localStorage-backed collapse mechanism (same
// LocalStorage helper, same account-scoped key shape, same cross-tab 'storage'
// listener) rather than introducing a second, competing persistence pattern.
const TOP_SECTIONS_STORAGE_KEY =
  LOCAL_STORAGE_KEYS.SIDEBAR_MINIMIZED_TOP_SECTIONS;

const getMinimizedTopSections = () => {
  const minimized = LocalStorage.get(TOP_SECTIONS_STORAGE_KEY);
  return minimized && typeof minimized === 'object' && !Array.isArray(minimized)
    ? minimized
    : {};
};

const minimizedTopSections = ref(getMinimizedTopSections());

const topSectionStorageKey = sectionKey =>
  accountId.value ? `${accountId.value}:${sectionKey}` : sectionKey;

const isSectionCollapsed = sectionKey =>
  Boolean(minimizedTopSections.value[topSectionStorageKey(sectionKey)]);

const toggleSection = sectionKey => {
  const storageKey = topSectionStorageKey(sectionKey);
  if (isSectionCollapsed(sectionKey)) {
    // Opening a section collapses every other one first, so only one
    // top-level section is ever expanded at a time — mirrors the single
    // shared `expandedItem` ref accordion SidebarGroup.vue already uses
    // for nested groups (see the GOTCHA above setExpandedItem: that one
    // never toggles-closed because a collapsed nested group still has to
    // peek its active child through; top-level sections have no such
    // peek-through — collapsed means fully hidden — so closing the
    // currently-open section on a second click is still fine here).
    SIDEBAR_SECTIONS.forEach(otherSection => {
      if (otherSection.key !== sectionKey) {
        LocalStorage.updateJsonStore(
          TOP_SECTIONS_STORAGE_KEY,
          topSectionStorageKey(otherSection.key),
          true
        );
      }
    });
    LocalStorage.deleteFromJsonStore(TOP_SECTIONS_STORAGE_KEY, storageKey);
  } else {
    LocalStorage.updateJsonStore(TOP_SECTIONS_STORAGE_KEY, storageKey, true);
  }
  minimizedTopSections.value = getMinimizedTopSections();
};

useEventListener(window, 'storage', event => {
  if (event.key === TOP_SECTIONS_STORAGE_KEY) {
    minimizedTopSections.value = getMinimizedTopSections();
  }
});

// [metrica] Without this, landing straight inside a top section that happens
// to be collapsed (deep link, Cmd+K, browser back/forward, a paywall
// redirect...) left the active page's own row unrendered in the sidebar --
// no "you are here" cue at all, unlike the nested SidebarGroup case, which
// already auto-expands on an active child (see its onMounted/watch
// hasActiveChild). Only fires when the target section is actually collapsed,
// so it never fights a section the user already has open.
watch(
  activeSectionKey,
  key => {
    if (key && isSectionCollapsed(key)) {
      toggleSection(key);
    }
  },
  { immediate: true }
);
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      {
        ignore: [
          '#mobile-sidebar-launcher',
          '[data-popover-content]',
          '[data-popover-backdrop]',
        ],
      },
    ]"
    class="bg-n-background metrica-chrome flex flex-col text-sm pb-px fixed top-0 ltr:left-0 rtl:right-0 h-full md:h-auto md:min-h-0 z-40 w-[200px] md:w-auto md:relative md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:translate-x-0 max-md:ltr:border-r max-md:rtl:border-l max-md:border-n-strong"
    :class="[
      {
        'shadow-lg md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
        'transition-transform duration-200 ease-out md:transition-[width]':
          !isResizing,
      },
    ]"
    :style="isMobile ? undefined : { width: `${sidebarWidth}px` }"
  >
    <!-- [metrica] The bg-n-alpha-1 tint is gone: on a single continuous plane it
         is decoration, and the number showed the signal was incoherent anyway —
         it composited LIGHTER in dark (+17) and DARKER in light (−5), i.e. the
         same class pointed opposite ways per theme. md:pt-* restores the 8px the
         removed md:m-2 used to give above the logo, desktop only. -->
    <section
      class="grid"
      :class="
        isEffectivelyCollapsed
          ? 'pt-3 md:pt-5 mb-6 gap-4'
          : 'pt-1 md:pt-3 mb-4 gap-2'
      "
    >
      <div
        class="flex gap-2 items-center min-w-0"
        :class="{
          'justify-center px-1': isEffectivelyCollapsed,
          'px-2': !isEffectivelyCollapsed,
        }"
      >
        <template v-if="isEffectivelyCollapsed">
          <SidebarAccountSwitcher
            is-collapsed
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
        <template v-else>
          <div class="grid flex-shrink-0 place-content-center size-6">
            <Logo class="size-4" />
          </div>
          <div class="flex-shrink-0 w-px h-3 bg-n-strong" />
          <SidebarAccountSwitcher
            class="flex-grow -mx-1 min-w-0"
            @show-create-account-modal="emit('showCreateAccountModal')"
          />
        </template>
      </div>
      <div
        class="flex gap-2"
        :class="isEffectivelyCollapsed ? 'flex-col items-center' : 'px-2'"
      >
        <RouterLink
          v-if="!isEffectivelyCollapsed"
          :to="{ name: 'search' }"
          class="flex gap-2 items-center px-2 py-1 w-full h-7 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-[outline-color,box-shadow] duration-150 ease-out hover:outline-n-strong hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-n-brand"
        >
          <span class="flex-shrink-0 i-lucide-search size-4 text-n-slate-10" />
          <span class="flex-grow text-start text-n-slate-10">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
          <span
            class="hidden tracking-wide pointer-events-none select-none text-n-slate-10"
          >
            {{ searchShortcut }}
          </span>
        </RouterLink>
        <RouterLink
          v-else
          :to="{ name: 'search' }"
          class="flex items-center justify-center size-8 rounded-lg outline outline-1 outline-n-weak bg-n-button-color transition-[outline-color,box-shadow,background-color] duration-150 ease-out hover:bg-n-alpha-2 hover:outline-n-strong hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-n-brand dark:hover:bg-n-slate-9/30"
          :title="t('COMBOBOX.SEARCH_PLACEHOLDER')"
        >
          <span class="i-lucide-search size-4 text-n-slate-11" />
        </RouterLink>
        <ComposeConversation align="start">
          <template #trigger="{ isOpen }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="dark:hover:!bg-n-slate-9/30"
              :class="[
                isEffectivelyCollapsed
                  ? '!size-8 !outline-n-weak !text-n-slate-11'
                  : '!h-7 !outline-n-weak !text-n-slate-11',
                { '!bg-n-alpha-2 dark:!bg-n-slate-9/30': isOpen },
              ]"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>
    <nav
      class="grid overflow-y-scroll flex-grow gap-2 pb-5 no-scrollbar min-w-0"
      :class="isEffectivelyCollapsed ? 'px-1' : 'px-2'"
      :aria-label="t('METRICA_SIDEBAR.MAIN_NAV')"
    >
      <ul
        class="flex flex-col gap-1 m-0 list-none min-w-0"
        :class="{ 'items-center': isEffectivelyCollapsed }"
      >
        <template
          v-for="(section, sectionIndex) in menuSections"
          :key="section.key"
        >
          <li v-if="isEffectivelyCollapsed && sectionIndex > 0" class="w-full">
            <div class="border-t border-n-strong mx-1 my-1" />
          </li>
          <!-- [metrica] Hairline between root sections in expanded mode
               (2026-07-23, user request): with every section collapsed, the
               four headers used to sit on bare `mt-5` spacing alone with no
               line between them, reading as one continuous list rather than
               four distinct groups. `border-n-weak` (not the rail divider's
               `border-n-strong` above) keeps it a subtle hairline rather than
               a rule, matching "linha fina e sutil". -->
          <li v-if="!isEffectivelyCollapsed && sectionIndex > 0" class="w-full">
            <div class="border-t border-n-weak mx-1.5 my-2" />
          </li>
          <!-- [metrica] Section headers used to be `text-xs uppercase
               tracking-wider` — an eyebrow, which is a LABEL by definition, and
               that is exactly why the resting state read as flat: with every
               section collapsed the entire sidebar was four labels and four
               rules. (It is also a named anti-pattern in this project's design
               skill.)

               A follow-up pass then gave the COLLAPSED state an outlined chip
               (`outline` + solid fill). That overcorrected: against the chrome
               the four outlines read as form fields floating in a void, and the
               user rightly called it out. Removed. What survives from that pass
               is what actually helped — sentence case, `text-sm`, a real row
               height, and a chevron that rotates instead of swapping icons.

               The OPEN section carries a NEUTRAL resting fill (bg-n-alpha-2 +
               slate-12 ink), not a blue tint. Since 2026-07-27 the brand blue is
               reserved for ONE thing — the current page (see the "Trilho Vivo"
               block in _metrica-theme.scss) — so tinting the open section blue too
               would split that signal across two rows. The chevron-down,
               single-open accordion and auto-open already say which section is
               open; the fill just seats it. Collapsed rows stay quieter.
               Weight and size stay constant so `truncate` never flickers on
               toggle; only ink and background move. -->
          <li v-if="!isEffectivelyCollapsed" class="w-full">
            <h3 class="contents">
              <button
                type="button"
                class="flex items-center justify-between gap-2 px-1.5 py-1 h-8 w-full rounded-lg text-sm font-520 text-start transition-[background-color,color] duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-n-brand"
                :class="
                  isSectionCollapsed(section.key)
                    ? 'text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12'
                    : 'bg-n-alpha-2 text-n-slate-12 hover:bg-n-alpha-3'
                "
                :aria-expanded="!isSectionCollapsed(section.key)"
                @click="toggleSection(section.key)"
              >
                <!-- [metrica] Section icon is size-4 (2026-07-27), matching the
                     item icons so section, item and leaf icon centres land on ONE
                     vertical axis — the "linha invisível" that makes the column
                     read as designed rather than default (a unanimous find of the
                     design panel; the old size-3.5 sat 1px off-axis). Header vs
                     row is still told apart by the heavier TEXT (font-520 vs the
                     item's font-420) + the chevron + the divider, not by icon
                     size. The icon inherits the row's colour (no class of its
                     own) so it seats neutrally with the de-tinted open section. -->
                <span class="size-4 flex-shrink-0" :class="section.icon" />
                <span class="flex-1 truncate min-w-0 text-start">
                  {{ t(section.labelKey) }}
                </span>
                <span
                  class="i-lucide-chevron-down size-3 flex-shrink-0 transition-[transform,background-color] duration-150 ease-[var(--m-ease-emphasized)]"
                  :class="{
                    'ltr:-rotate-90 rtl:rotate-90': isSectionCollapsed(
                      section.key
                    ),
                  }"
                />
              </button>
            </h3>
          </li>
          <!-- Section-collapse only applies in expanded mode, where the
               toggle button (above) is reachable. In collapsed/icon-rail
               mode there is no button to re-open a section, so a section
               closed while expanded would leave its icons permanently
               stuck hidden after switching to rail mode — always show all
               icons there regardless of the collapse state. -->
          <template
            v-if="isEffectivelyCollapsed || !isSectionCollapsed(section.key)"
          >
            <!-- [metrica] Stagger de entrada (2026-07-27): ao abrir uma seção o
                 <template v-if> remonta seus SidebarGroup, então animate-fade-in-up
                 dispara na montagem. `motion-safe:` + o guard de reduced-motion no
                 tema cobrem a11y; animation-fill-mode:both segura opacity:0 durante
                 o delay (senão pisca o estado final). Reusa o keyframe fade-in-up já
                 no bundle (tailwind.config), sem duplicar. class/style caem no <li>
                 via fall-through por SidebarGroup->Policy (ambos single-root). -->
            <SidebarGroup
              v-for="(item, itemIndex) in section.items"
              :key="item.name"
              v-bind="item"
              :class="
                !isEffectivelyCollapsed ? 'motion-safe:animate-fade-in-up' : ''
              "
              :style="
                !isEffectivelyCollapsed
                  ? {
                      animationDelay: `${itemIndex * 35}ms`,
                      animationFillMode: 'both',
                    }
                  : {}
              "
            />
          </template>
        </template>
      </ul>
    </nav>
    <section
      class="flex relative flex-col flex-shrink-0 gap-1 justify-between items-center"
    >
      <!-- [metrica] Re-anchored to the chrome. The Tailwind utilities stay as
           the fallback; without this the fade would still resolve to
           --background-color and leave a visible 32px seam right above the
           profile row now that the sidebar is filled with the chrome. -->
      <div
        class="metrica-chrome-fade-t pointer-events-none absolute inset-x-0 -top-[1.938rem] h-8 bg-gradient-to-t from-n-background to-transparent"
      />
      <SidebarChangelogCard
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          !isEffectivelyCollapsed
        "
      />
      <SidebarChangelogButton
        v-if="
          isOnChatwootCloud &&
          !isACustomBrandedInstance &&
          isEffectivelyCollapsed
        "
      />
      <div
        class="px-1 py-1.5 md:pb-3.5 flex-shrink-0 flex w-full z-50 gap-2 items-center border-t border-n-weak"
        :class="isEffectivelyCollapsed ? 'justify-center' : 'justify-between'"
      >
        <SidebarProfileMenu
          :is-collapsed="isEffectivelyCollapsed"
          @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        />
      </div>
    </section>
    <!-- Resize Handle (desktop only). Runs the full height: the top-4/bottom-4
         inset existed only to clear the arcs of the md:rounded-2xl the aside no
         longer has. This strip is the ONLY line that ever appears on the
         sidebar↔content boundary, and only on hover — by design the resting
         boundary is the edge of the content sheet itself, 8px to the right.
         Drawing a hairline here would recreate the "two materials" reading that
         caused the complaint in the first place. -->
    <div
      class="hidden md:block absolute inset-y-0 w-1 cursor-col-resize z-40 ltr:right-0 rtl:left-0 group"
      @mousedown="onResizeStart"
      @touchstart="onResizeStart"
      @dblclick="onResizeHandleDoubleClick"
    >
      <div
        class="absolute top-0 h-full w-px ltr:right-0 rtl:left-0 bg-transparent group-hover:bg-n-brand transition-colors"
        :class="{ 'bg-n-brand': isResizing }"
      />
    </div>
    <!-- Explicit click target for the same toggle as the resize handle's
         double-click — dragging alone is low-discoverability.
         MATH (redone; the old "size/2 + inset" form only held while the aside
         and <main> carried EQUAL margins):
           |right| = buttonWidth/2 + gutter/2,   gutter = <main>'s md:m-2
         The aside no longer has a margin OR a border on desktop (it is the
         floor of the app and bleeds to the window edge), so its padding box
         coincides with its border box and the chrome gutter between the two is
         exactly <main>'s md:m-2 = 8px. Hence |right| = 12 + 4 = 16px
         (-right-4): the circle's right edge lands at asideRight+16, its left at
         asideRight-8, its CENTER at asideRight+4 — the exact middle of the 8px
         gutter. (As a side effect this also fixes the pre-existing 1px error,
         where the aside's own border shifted the padding box.) If <main>'s
         md:m-2 ever changes, recompute with the formula above: a 16px gutter
         gives 12+8 = 20px = -right-5, which was the previous value.
         The button STRADDLES the seam deliberately: 24px across an 8px gutter
         necessarily touches both planes, and that is the reading — the one
         object belonging to both the sidebar and the sheet (same pattern as
         VS Code / Notion). p-0 is required: _base.scss's unconditional
         `button { ... px-2.5 py-1 ... }` base rule has nothing else overriding
         it on this custom button, so without p-0 that padding eats almost the
         entire size-6 box and the chevron renders invisible (only the circle
         bg/border show) — the same defense Button.vue's own iconOnly sizes
         already bake in. -->
    <button
      type="button"
      class="hidden md:flex absolute top-1/2 -translate-y-1/2 items-center justify-center size-6 p-0 rounded-full border border-n-weak bg-n-solid-2 text-n-slate-11 shadow-sm z-50 transition-transform duration-150 active:scale-95 hover:bg-n-alpha-2 hover:text-n-slate-12 ltr:-right-4 rtl:-left-4"
      :title="
        t(
          isEffectivelyCollapsed
            ? 'METRICA_SIDEBAR.EXPAND'
            : 'METRICA_SIDEBAR.COLLAPSE'
        )
      "
      @click="onResizeHandleDoubleClick"
    >
      <span
        v-if="collapseIconPointsRight"
        class="i-lucide-chevron-right size-4"
      />
      <span v-else class="i-lucide-chevron-left size-4" />
    </button>
  </aside>
</template>
